import { FEATURE_MATRIX } from '@/constants/plan-feature-matrix';
import type { Plan, TrialRenewal, TrialState } from '@/types';

export const CARD_TRIAL_DAYS = 14;

/**
 * A trial this many days or fewer from its end reads as ending soon (amber)
 * everywhere it shows: the trial bar, the sidebar's Upgrade button and the
 * plan card.
 */
export const TRIAL_ENDING_SOON_DAYS = 3;

/** Every trial's credit grant (the backend's `TRIAL_CREDITS`); the payload's
 *  `trial.credits_granted` wins once it loads. */
export const TRIAL_CREDITS = 500;

/** Pricing v4 Pro trial: credits once a card is saved (the API's offer). */
export const PRO_TRIAL_CREDITS = 250;

/** Extra Pro-trial credits for a work-email signup. */
export const WORK_EMAIL_BONUS_CREDITS = 100;

/** One-time reward for the onboarding call (the API's
 *  `ONBOARDING_CALL_REWARD_CREDITS`), granted once staff approve it. */
export const ONBOARDING_CALL_CREDITS = 500;

export const formatPlanPrice = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

/** "$79/mo" or "$711/yr". */
export const formatRenewalPrice = (renewal: TrialRenewal) =>
  `${formatPlanPrice(renewal.price_cents)}/${renewal.interval === 'year' ? 'yr' : 'mo'}`;

/** "Oct 13". */
export const formatShortDate = (date: Date | string | number) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
    new Date(date)
  );

/** "Plus 1 extra seat at $15/month", or undefined when the seats fit the plan. */
export const extraSeatNote = (
  seats: number,
  baseSeats: number,
  seatPriceCents: number | undefined
) => {
  const extra = seats - baseSeats;
  if (extra <= 0 || !seatPriceCents) return undefined;
  return `Plus ${extra} extra seat${extra === 1 ? '' : 's'} at ${formatPlanPrice(seatPriceCents)}/month`;
};

export const trialParam = () =>
  typeof window === 'undefined'
    ? null
    : new URLSearchParams(window.location.search).get('trial');

/** The card-required trial's flag: the backend reads it at signup to record
 *  the offer, and the offer banner reads the same key. */
export const CARD_TRIAL_FLAG = 'trial_card_required_v1';

// Set when onboarding finishes: the full-screen offer stays up in this tab
// until the user starts the trial or continues on Free.
const OFFER_PENDING_KEY = 'cl_card_trial_offer_pending';

export function markTrialOfferPending() {
  try {
    sessionStorage.setItem(OFFER_PENDING_KEY, '1');
  } catch {
    // Storage unavailable: the banner still offers the trial.
  }
}

export function trialOfferPending() {
  try {
    return sessionStorage.getItem(OFFER_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearTrialOfferPending() {
  try {
    sessionStorage.removeItem(OFFER_PENDING_KEY);
  } catch {
    // Nothing to clear.
  }
}

// Set when onboarding finishes: the onboarding call follows the trial decision
// (survey -> trial / Stripe -> call -> app). localStorage, so it survives the
// Stripe round trip. It holds the user's id, so another account signed in on
// this browser (the booking page offers Sign out) never inherits it.
const BOOKING_PENDING_KEY = 'cl_post_trial_booking';

export function markBookingPending(userId: string | undefined) {
  if (!userId) return;
  try {
    localStorage.setItem(BOOKING_PENDING_KEY, userId);
  } catch {
    // Storage unavailable: straight to the app, as before.
  }
}

export function bookingPending(userId: string | undefined) {
  if (!userId) return false;
  try {
    return localStorage.getItem(BOOKING_PENDING_KEY) === userId;
  } catch {
    return false;
  }
}

export function clearBookingPending() {
  try {
    localStorage.removeItem(BOOKING_PENDING_KEY);
  } catch {
    // Nothing to clear.
  }
}

type TierKey = 'starter' | 'pro' | 'scale';

/**
 * Scale features the trial has that `tier` does not, from the /plans matrix.
 * API access is withheld during a trial, and volume rates and priority support
 * are terms rather than product features, so none of them are "lost".
 */
export function featuresLostOnStart(tier: string): string[] {
  if (!['starter', 'pro', 'scale'].includes(tier)) return [];
  return FEATURE_MATRIX.filter(
    (row) =>
      row.scale === true &&
      row[tier as TierKey] !== true &&
      ![
        'API Access & Webhooks',
        'Volume Pricing Rates',
        'Priority Support',
      ].includes(row.feature)
  ).map((row) => row.feature);
}

/**
 * The pricing v4 numbers the Pro trial page shows, read from the API: the
 * trial block of /billing/feature-access and the v4 /billing/plans catalog.
 * Null where the API hasn't sent one, so the page shows a dash rather than a
 * copy that can drift from what the API grants.
 */
export interface ProTrialTerms {
  trialCredits: number | null;
  trialSearches: number | null;
  trialSeats: number | null;
  /** Dollars per seat a month. */
  starterPrice: number | null;
  starterCreditsPerSeat: number | null;
  starterSearches: number | null;
  freeCredits: number | null;
  freeSearches: number | null;
  freeSeats: number | null;
}

export function proTrialTerms(
  trial: TrialState | null | undefined,
  plans: readonly Plan[] | null | undefined
): ProTrialTerms {
  const v4 = (tier: Plan['tier_key']) =>
    plans?.find((p) => p.pricing_version === 'v4' && p.tier_key === tier);
  const starter = v4('starter');
  const free = v4('free');
  return {
    trialCredits: trial?.credits_granted ?? null,
    trialSearches: trial?.searches_granted ?? null,
    trialSeats: trial?.seat_limit ?? null,
    starterPrice: starter?.monthly_price?.price ?? null,
    starterCreditsPerSeat: starter?.credits_per_seat ?? null,
    starterSearches: starter?.monthly_searches ?? null,
    freeCredits: free?.monthly_credits ?? null,
    freeSearches: free?.monthly_searches ?? null,
    freeSeats: free?.base_seats ?? null,
  };
}

/** A count from `ProTrialTerms`, or a dash while the API hasn't sent it. */
export function termCount(value: number | null, suffix = ''): string {
  return value == null ? '—' : `${value.toLocaleString('en-US')}${suffix}`;
}
